import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

export function CountCardHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number; arrow: number; above: boolean } | null>(null);

  const position = () => {
    const button = buttonRef.current;
    const tip = tipRef.current;
    if (!button || !tip) return;
    const rect = button.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const main = button.closest('main');
    const mainRect = main?.getBoundingClientRect();
    const minLeft = (mainRect?.left ?? 0) + 12;
    const maxRight = Math.min(window.innerWidth, mainRect?.right ?? window.innerWidth) - 12;
    let left = rect.right - width;
    if (left < minLeft) left = minLeft;
    if (left + width > maxRight) left = Math.max(minLeft, maxRight - width);
    const gap = 8;
    let top = rect.top - height - gap;
    let above = true;
    if (top < 8) {
      top = rect.bottom + gap;
      above = false;
    }
    const arrow = Math.min(width - 14, Math.max(14, rect.left + rect.width / 2 - left));
    setPlace({ top, left, arrow, above });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    position();
  }, [open, text]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || tipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const reposition = () => position();
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  return (
    <div
      className="relative shrink-0 self-end"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label="What this count means"
        onClick={() => setOpen((current) => !current)}
        className={`p-0.5 rounded-full ${open ? 'bg-blue-50' : 'hover:bg-slate-100'}`}
      >
        <Info className={`w-3.5 h-3.5 ${open ? 'text-blue-600' : 'text-slate-400'}`} />
      </button>
      {open && createPortal(
        <div
          ref={tipRef}
          style={{ top: place?.top ?? -9999, left: place?.left ?? 0 }}
          className={`fixed z-[200] w-52 p-2.5 bg-slate-800 text-white text-[11px] font-medium leading-relaxed rounded-lg shadow-lg ${place ? '' : 'invisible'}`}
        >
          {text}
          <div
            className={`absolute w-0 h-0 border-4 border-transparent ${place?.above === false ? 'bottom-full border-b-slate-800' : 'top-full border-t-slate-800'}`}
            style={{ left: (place?.arrow ?? 16) - 4 }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
